// vendors
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
// libs
import { DOCUMENT } from '@angular/common';
import { CellExportFacade, NotebookTypes } from '@mode/notebooks/contract';
import { FeatureFlag } from '@mode/shared/contract-common';
import { getInitialFlagValue, getThoughtspotIntegrated, MetaTags, MetaTagsService } from '@mode/shared/util-js';
import { Observable } from 'rxjs';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';

@Component({
  selector: 'mode-notebook-cell-menu',
  templateUrl: './notebook-cell-menu.component.html',
  styleUrls: ['./notebook-cell-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotebookCellMenuComponent implements OnInit, OnChanges {
  enableNotebookExportDatasets = getInitialFlagValue(FeatureFlag.NotebookExportDatasets);

  @Input()
  cellToken!: string;

  @Input()
  contentType!: NotebookTypes.ContentType;

  @Input()
  prevCellToken?: string;

  @Input()
  nextCellToken?: string;

  @Input()
  hasResult = false;

  @Input()
  includedInReport = false;

  @Input()
  isFolded = false;

  @Input()
  isFrozen = false;

  @Input()
  canRun = false;

  @Input()
  canFreeze = true;

  @Input()
  canDelete = true;

  @Output()
  runCell: EventEmitter<string> = new EventEmitter();

  @Output()
  moveCell: EventEmitter<'up' | 'down'> = new EventEmitter();

  @Output()
  deleteCell: EventEmitter<void> = new EventEmitter();

  @Output()
  toggleFreezeState: EventEmitter<void> = new EventEmitter();

  @Output()
  toggleFoldState: EventEmitter<void> = new EventEmitter();

  @Output()
  toggleIncludedInReportState: EventEmitter<boolean> = new EventEmitter();

  @Output()
  insertCellAbove: EventEmitter<string> = new EventEmitter();

  @Output()
  insertCellBelow: EventEmitter<string> = new EventEmitter();

  @Output()
  contentTypeChange: EventEmitter<NotebookTypes.ContentType> = new EventEmitter();

  isExporting$!: Observable<boolean>;

  isCellExported$!: Observable<boolean>;

  cellContentType?: NotebookTypes.ContentType;
  thoughtspotIntegrated = getThoughtspotIntegrated(this.document);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private cellExportFacade: CellExportFacade,
    private analyticsService: AnalyticsService,
    private metaTag: MetaTagsService
  ) {}

  ngOnInit(): void {
    this.isExporting$ = this.cellExportFacade.isExportingByCell$(this.cellToken);
    this.isCellExported$ = this.cellExportFacade.selectedCellIsExported$;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['contentType']) {
      this.cellContentType = changes['contentType'].currentValue;
    }
  }

  run(nextCellToken: string) {
    this.runCell.emit(nextCellToken);
  }

  moveCellDirection(direction: 'up' | 'down') {
    if (!this.cellToken) {
      return;
    }
    this.moveCell.emit(direction);
  }

  delete() {
    if (!this.cellToken) {
      return;
    }
    const eventData = {
      context: this.analyticsService.context,
      location: `${AnalyticsEvent.SubContext.NotebookEditor} delete cell CTA`,
      action: AnalyticsEvent.Name.NotebookCell,
      name: this.metaTag.getTag(MetaTags.CurrentUsername),
    };
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, eventData);
    this.deleteCell.emit();
  }

  toggleFreezeCell() {
    if (!this.cellToken) {
      return;
    }
    this.toggleFreezeState.emit();
  }

  toggleFoldCell() {
    if (!this.cellToken) {
      return;
    }
    this.toggleFoldState.emit();
  }

  toggleIncluedInReport(params: { cellToken: string; inLayout: boolean }) {
    const { inLayout } = params;

    if (!this.cellToken) {
      return;
    }
    this.toggleIncludedInReportState.emit(inLayout);
  }

  cellAbove() {
    if (!this.cellToken) {
      return;
    }

    this.insertCellAbove.emit(this.cellToken);
  }

  cellBelow() {
    this.insertCellBelow.emit(this.nextCellToken);
  }

  updateContentType() {
    this.contentTypeChange.emit(this.cellContentType);
  }
}
